/* ============================================================
   Market Dashboard v3 — helpers + shared primitives.
   Converted from prototype window globals to ES modules.
   ============================================================ */
import React from 'react';

export const SIG_META = {
  discovery: { label: 'Discovery', color: 'var(--sig-discovery)', cls: 'discovery' },
  discovery_suppressed: { label: 'Disc. gated', color: 'var(--sig-discovery)', cls: 'discovery' },
  discovery_outlier_suppressed: { label: 'Outlier gated', color: 'var(--sig-discovery)', cls: 'discovery' },
  jump:      { label: 'Jump',      color: 'var(--sig-jump)',      cls: 'jump' },
  jump_decay:{ label: 'Jump decay',color: 'var(--sig-vig-tight)', cls: 'jump-decay' },
  'vig-wide':{ label: 'Vig widen', color: 'var(--sig-vig-wide)',  cls: 'vig-wide' },
  'vig-tight':{label: 'Vig tighten',color:'var(--sig-vig-tight)', cls: 'vig-tight' },
  dropout:   { label: 'Dropout',   color: 'var(--sig-dropout)',   cls: 'dropout' },
  alt_flick: { label: 'Alt flick', color: 'var(--sig-dropout)',   cls: 'dropout' },
  resume:    { label: 'Resume',    color: 'var(--sig-resume)',    cls: 'resume' },
  line:      { label: 'Line chg',  color: 'var(--sig-line)',      cls: 'line' },
  seed:      { label: 'Seed',      color: 'var(--sig-seed)',      cls: 'seed' },
  entries_fuse: { label: 'Comp price update', color: 'var(--sig-discovery)', cls: 'discovery' },
  panel:     { label: 'Panel',     color: 'var(--sig-seed)',      cls: 'seed' },
  tob_cap:   { label: 'TOB cap',   color: 'var(--sig-vig-wide)',  cls: 'vig-wide' },
  tob_exhaust: { label: 'TOB exhaust', color: 'var(--sig-dropout)', cls: 'dropout' },
  tob_clear: { label: 'TOB clear', color: 'var(--sig-resume)',    cls: 'resume' },
  none:      { label: 'No signal', color: 'var(--sig-none)',      cls: 'none' },
};
export const SIG_HEX = {
  discovery: '#60A5FA',
  discovery_suppressed: '#86EFAC',
  discovery_outlier_suppressed: '#86EFAC',
  jump: '#f59e0b', jump_decay: '#34d399', 'vig-wide': '#fb7185', 'vig-tight': '#34d399',
  dropout: '#c084fc', alt_flick: '#c084fc', resume: '#34d399', line: '#f59e0b', seed: '#94a3b8',
  entries_fuse: '#38BDF8', panel: '#64748B',
  tob_cap: '#fb7185', tob_exhaust: '#c084fc', tob_clear: '#34d399',
  none: '#64748b',
};

export function dvMoney(n, { sign = false } = {}) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n), abs = Math.abs(v);
  const s = '$' + abs.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (!sign) return s;
  return v > 0 ? '+' + s : v < 0 ? '−' + s : s;
}
export function dvK(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n), abs = Math.abs(v), sgn = v < 0 ? '−' : '';
  return abs >= 1000 ? sgn + '$' + (abs / 1000).toFixed(1) + 'k' : sgn + '$' + abs.toFixed(0);
}
export function dvPct(x, d = 1) { return x == null || !Number.isFinite(Number(x)) ? '—' : (Number(x) * 100).toFixed(d) + '%'; }
export function dvPp(x, d = 1) {
  if (x == null || !Number.isFinite(Number(x))) return '—';
  const v = Number(x);
  return (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(v).toFixed(d) + 'pp';
}

/** Prefer converted decimal odds (Underdog mult → decimal); fall back to raw posted. */
export function displayDecimalOdds(raw, converted) {
  const c = nn(converted);
  if (c != null && c > 1) return c;
  return nn(raw);
}

function nn(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function moved(from, to, eps = 1e-9) {
  if (from == null || to == null) return false;
  return Math.abs(from - to) > eps;
}

export function signalType(row) {
  const ev = String(row?.event || '').toLowerCase();
  if (ev === 'entries_fuse') return 'entries_fuse';
  if (ev === 'panel') return 'panel';
  if (ev === 'tob_cap') return 'tob_cap';
  if (ev === 'tob_exhaust') return 'tob_exhaust';
  if (ev === 'tob_clear') return 'tob_clear';
  if (ev === 'd_out_sup' || ev.endsWith('_d_out_sup')) return 'discovery_outlier_suppressed';
  if (ev === 'd_sup' || ev === 'd_conv_sup' || ev.endsWith('_d_sup') || ev.endsWith('_d_conv_sup')) {
    return 'discovery_suppressed';
  }
  // Resume before dropout: do_resume / dropout_feed_resume both contain "drop".
  if (ev === 'do_resume' || ev === 'dropout_feed_resume' || ev.includes('feed_resume')) return 'resume';
  if (ev === 'alt_flick_sup' || ev === 'alt_flicker_suppression') return 'alt_flick';
  if (ev === 'dropout') return 'dropout';
  if (ev === 'jump_decay') return 'jump_decay';
  const raw = String(row?.event || row?.signal_definition || row?.ingest_update_type || '').toLowerCase();
  if (raw.includes('resume')) return 'resume';
  if (raw.includes('alt_flick')) return 'alt_flick';
  if (raw === 'dropout' || (raw.includes('dropout') && !raw.includes('resume'))) return 'dropout';
  if (raw.includes('vig') && (raw.includes('wide') || nn(row?.vig_to) > nn(row?.vig_from))) return 'vig-wide';
  if (raw.includes('vig')) return 'vig-tight';
  if (raw.includes('line')) return 'line';
  if (raw.includes('jump') || nn(row?.d2) >= 4) return 'jump';
  if (raw.includes('seed')) return 'seed';
  if (raw.includes('entries_fuse') || raw.includes('fuse')) return 'entries_fuse';
  if (raw === 'panel' || raw.includes('panel book')) return 'panel';
  if (raw.includes('discover') || raw.includes('book')) return 'discovery';
  return 'none';
}

/** Human-readable summary of what changed on the competitor book. */
export function formatLedgerBookMove(row, type = signalType(row)) {
  const lineFrom = nn(row?.source_line_from);
  const lineTo = nn(row?.source_line_to);
  const poFrom = displayDecimalOdds(row?.price_over_from, row?.decimal_over_from);
  const poTo = displayDecimalOdds(row?.price_over_to, row?.decimal_over_to);
  const puFrom = displayDecimalOdds(row?.price_under_from, row?.decimal_under_from);
  const puTo = displayDecimalOdds(row?.price_under_to, row?.decimal_under_to);
  const pfFrom = nn(row?.p_fair_quote_from);
  const pfTo = nn(row?.p_fair_quote_to);
  const dm = nn(row?.delta_mid);
  const vigFrom = nn(row?.vig_from);
  const vigTo = nn(row?.vig_to);
  const parts = [];

  if (type === 'jump_decay') return 'time-based excess unwind';

  if (type === 'tob_cap' || type === 'tob_exhaust' || type === 'tob_clear') {
    const bits = [];
    if (poTo != null) bits.push(`over ${poTo.toFixed(2)}`);
    if (puTo != null) bits.push(`under ${puTo.toFixed(2)}`);
    if (pfTo != null) bits.push(`fair ${(pfTo * 100).toFixed(1)}%`);
    return bits.length ? bits.join(' · ') : (row?.signal_definition || type);
  }

  if (moved(lineFrom, lineTo)) {
    parts.push(`line ${lineFrom}→${lineTo}`);
  }

  if (type === 'dropout') {
    const line = lineFrom ?? lineTo;
    const over = poFrom ?? poTo;
    const under = puFrom ?? puTo;
    const price = over != null
      ? `${over.toFixed(2)}${under != null ? ` / ${under.toFixed(2)}` : ''}`
      : null;
    return `left @ ${line ?? '—'}${price ? ` · ${price}` : ''}`;
  }

  if (type === 'resume') {
    const line = lineTo ?? lineFrom;
    const over = poTo ?? poFrom;
    const under = puTo ?? puFrom;
    const price = over != null
      ? `${over.toFixed(2)}${under != null ? ` / ${under.toFixed(2)}` : ''}`
      : null;
    return `rejoined @ ${line ?? '—'}${price ? ` · ${price}` : ''}`;
  }

  const priceParts = [];
  if (moved(poFrom, poTo, 0.001)) priceParts.push(`O ${poFrom.toFixed(2)}→${poTo.toFixed(2)}`);
  if (moved(puFrom, puTo, 0.001)) priceParts.push(`U ${puFrom.toFixed(2)}→${puTo.toFixed(2)}`);
  if (priceParts.length) parts.push(priceParts.join(' · '));

  if (moved(pfFrom, pfTo, 1e-6)) {
    parts.push(`fair ${dvPct(pfFrom, 1)}→${dvPct(pfTo, 1)}`);
  } else if (dm != null && Math.abs(dm) > 1e-6) {
    parts.push(`Δmid ${dm > 0 ? '+' : '−'}${Math.abs(dm * 100).toFixed(2)}pp`);
  }

  if (moved(vigFrom, vigTo, 1e-6) && (type === 'vig-wide' || type === 'vig-tight')) {
    parts.push(`vig ${(vigFrom * 100).toFixed(1)}%→${(vigTo * 100).toFixed(1)}%`);
  }

  if (parts.length) return parts.join(' · ');

  const line = lineTo ?? lineFrom;
  const over = poTo ?? poFrom;
  const under = puTo ?? puFrom;
  if (over != null) {
    const price = `${over.toFixed(2)}${under != null ? ` / ${under.toFixed(2)}` : ''}`;
    return `@ ${line ?? '—'} · ${price}`;
  }
  return '—';
}

export function hasLedgerImpact({ dx, dP, gated, type, d2, mad_z }) {
  if (Math.abs(dx ?? 0) > 0.0005 || Math.abs(dP ?? 0) > 0.0005) return true;
  if (gated) return true;
  if (type === 'jump' && d2 != null && d2 > 0) return true;
  if ((type === 'discovery_suppressed' || type === 'discovery_outlier_suppressed') && mad_z != null) return true;
  return false;
}

/** How this signal moved our Entries estimate (prob x and variance P). */
export function formatLedgerImpact({
  dx,
  dP,
  gated,
  type,
  d2,
  mad_z,
}) {
  const parts = [];
  if (Math.abs(dx ?? 0) > 0.0005) parts.push(`Δx ${dvPp(dx)}`);
  if (Math.abs(dP ?? 0) > 0.0005) {
    parts.push(`ΔP ${dP > 0 ? '+' : '−'}${Math.abs(dP).toFixed(3)}`);
  }
  if (parts.length) {
    if (type === 'jump' && d2 != null && d2 > 0) parts.push(`d² ${d2.toFixed(1)}`);
    return parts.join(' · ');
  }
  if (gated) return 'gated — no move';
  if (type === 'dropout') return 'variance already credited';
  if (type === 'discovery_suppressed' || type === 'discovery_outlier_suppressed') {
    return mad_z != null
      ? `suppressed · z_mad ${mad_z > 0 ? '+' : ''}${mad_z.toFixed(1)}`
      : 'suppressed';
  }
  if (type === 'resume') return 'dropout credit reversed';
  return 'no impact';
}

/* liability classing (ports markets-li-sign / -wgt) */
export function liClass(v, maxAbs) {
  const n = Number(v);
  const sign = !Number.isFinite(n) ? 'li-zero' : n > 1e-9 ? 'li-pos' : n < -1e-9 ? 'li-neg' : 'li-zero';
  const av = Number.isFinite(n) ? Math.abs(n) : 0;
  let w = 'li-lo';
  if (Number.isFinite(maxAbs) && maxAbs > 1e-9) { const r = av / maxAbs; w = r >= 0.72 ? 'li-hi' : r >= 0.22 ? 'li-mid' : 'li-lo'; }
  return `${sign} ${w}`;
}

export function SigChip({ type }) {
  const m = SIG_META[type] || SIG_META.none;
  return <span className={`sig-chip sig-chip--${m.cls}`}><span className="gly" style={{ background: m.color }} />{m.label}</span>;
}

/* effect cell: Δx (prob pts) and ΔP */
export function EffectCells({ dx, dP }) {
  const xDir = dx == null ? 'flat' : dx > 0.05 ? 'up' : dx < -0.05 ? 'down' : 'flat';
  const pDir = dP == null ? 'flat' : dP > 0.0005 ? 'up' : dP < -0.0005 ? 'down' : 'flat';
  return (
    <span className="ledger-effect">
      <span className="eff"><span className="eff-label">Δx</span><span className={`eff-x ${xDir}`}>{dx == null ? '—' : dvPp(dx)}</span></span>
      <span className="eff"><span className="eff-label">ΔP</span><span className={`eff-p ${pDir}`}>{dP == null ? '—' : (dP > 0 ? '+' : dP < 0 ? '−' : '') + Math.abs(dP).toFixed(3)}</span></span>
    </span>
  );
}

/* Annotated probability chart (hand-built SVG, no deps).
   P(fair over) line + CI band + signal-event markers. */
export function ProbChart({ series, markers, height = 250 }) {
  const W = 720, H = height, padL = 8, padR = 8, padT = 16, padB = 22;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  let lo = Math.min(...series.map((s) => s.lo)), hi = Math.max(...series.map((s) => s.hi));
  const pad = (hi - lo) * 0.18 || 0.05; lo -= pad; hi += pad;
  const X = (x) => padL + x * innerW;
  const Y = (p) => padT + (1 - (p - lo) / (hi - lo)) * innerH;

  const linePath = series.map((s, i) => `${i ? 'L' : 'M'}${X(s.x).toFixed(1)},${Y(s.p).toFixed(1)}`).join(' ');
  const bandPath =
    series.map((s, i) => `${i ? 'L' : 'M'}${X(s.x).toFixed(1)},${Y(s.hi).toFixed(1)}`).join(' ') +
    ' ' + [...series].reverse().map((s) => `L${X(s.x).toFixed(1)},${Y(s.lo).toFixed(1)}`).join(' ') + ' Z';

  const ticks = [];
  for (let k = 0; k <= 4; k++) { const p = lo + (k / 4) * (hi - lo); ticks.push(p); }

  return (
    <div>
      <svg className="prob-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height }}>
        {ticks.map((p, i) => (
          <g key={i}>
            <line x1={padL} y1={Y(p)} x2={W - padR} y2={Y(p)} stroke="rgba(96,165,250,0.12)" strokeWidth="1" />
            <text x={W - padR} y={Y(p) - 2} fill="rgba(148,163,184,0.7)" fontSize="9" textAnchor="end" fontFamily="ui-monospace, monospace">{(p * 100).toFixed(0)}%</text>
          </g>
        ))}
        {markers.filter((m) => m.type === 'dropout').map((m, i) => {
          const s = series[m.i];
          const next = series[Math.min(m.i + 1, series.length - 1)];
          return <rect key={`d${i}`} x={X(s.x)} y={padT} width={Math.max(6, X(next.x) - X(s.x))} height={innerH} fill="rgba(192,132,252,0.12)" />;
        })}
        <path d={bandPath} fill="rgba(180,83,9,0.14)" stroke="none" />
        <path d={linePath} fill="none" stroke="#d97706" strokeWidth="2.2" />
        {markers.map((m, i) => {
          const s = series[m.i];
          const big = m.type === 'jump';
          return (
            <g key={i}>
              <line x1={X(s.x)} y1={Y(s.p)} x2={X(s.x)} y2={H - padB} stroke={SIG_HEX[m.type]} strokeWidth="1" strokeDasharray="2 2" opacity="0.5" />
              <circle cx={X(s.x)} cy={Y(s.p)} r={big ? 6 : 4} fill={SIG_HEX[m.type]} stroke="#0f172a" strokeWidth="1.5" />
            </g>
          );
        })}
        <circle cx={X(1)} cy={Y(series[series.length - 1].p)} r="3.5" fill="#fff" />
      </svg>
      <div className="prob-chart-legend">
        <span className="li"><span className="sw" style={{ background: '#d97706' }} />P(fair over)</span>
        <span className="li"><span className="sw" style={{ background: 'rgba(180,83,9,0.4)', height: 8 }} />90% CI</span>
        <span className="li"><span className="mk" style={{ background: SIG_HEX.jump }} />Jump</span>
        <span className="li"><span className="mk" style={{ background: SIG_HEX.discovery }} />Discovery</span>
        <span className="li"><span className="mk" style={{ background: SIG_HEX.dropout }} />Dropout</span>
        <span className="li"><span className="mk" style={{ background: SIG_HEX.line }} />Line chg</span>
        <span className="li"><span className="mk" style={{ background: SIG_HEX['vig-wide'] }} />Vig</span>
      </div>
    </div>
  );
}
